import { http, String as Strings, Ui } from '@silvertours/front-shared';
import { format as dateFormat } from 'date-fns/format';
import { useEffect, useState } from 'react';
import { useFormatter } from 'next-intl';
import { useConfig, useLocale } from '../Runtime';
import {
  CurrentRating,
  AggregatedRating,
  Name,
  StyledThirdPartyLogo,
  RatingLine,
  RatingsList,
  ReviewBody,
  ReviewHeader,
  ReviewList,
  Stars,
  StyledReviewsChart,
  Summary,
  ToggleChartButton,
  Title,
} from './ReviewOverlay.styles';
import {
  RatingChartTranslations,
  SupplierDetails,
  SupplierReview,
} from './types';
import { trackReviewShowDetailsClicked } from '../analytics/gtm';
import { generic } from '../analytics';

type Translations = {
  chart: RatingChartTranslations;
  title: string;
  showMore: string;
  showLess: string;
  rating: string;
  ratings: string;
  stars: string;
};

type Props = {
  localization: string;
  supplier: string;
  cityId?: string;
  stateId?: string;
  countryCode?: string;
  onClose: () => void;
  translations: Translations;
};

const formatValue = (
  formatter: ReturnType<typeof useFormatter>,
  value: number,
  minDigits = 1,
  maxDigits = 1,
) =>
  formatter.number(value, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  });

export const Review = ({
  review,
  translations,
}: {
  review: SupplierReview;
  translations: Translations;
}) => {
  const format = useFormatter();
  const { language } = useLocale();
  const [showChart, setShowChart] = useState(false);

  const openChart = () => {
    trackReviewShowDetailsClicked();
    generic.trackReviewShowDetailsClicked();
    setShowChart(true);
  };

  return (
    <article>
      <ReviewHeader>
        <RatingLine value={formatValue(format, review.totalRating.value)}>
          <CurrentRating>
            {formatValue(format, review.totalRating.value)}
          </CurrentRating>
          /{formatValue(format, review.totalRating.max)}
        </RatingLine>
        <Stars value={review.totalRating.value} label={translations.stars} />
        <span>
          <time
            dateTime={dateFormat(
              review.date,
              Strings.dateFormats[language].isoDate,
            )}
          >
            {dateFormat(review.date, Strings.dateFormats[language].date)}
          </time>{' '}
          | {review.customerName}
        </span>
      </ReviewHeader>
      <ReviewBody>{review.quote}</ReviewBody>
      <footer>
        {showChart ? (
          <>
            <StyledReviewsChart
              translations={translations.chart}
              ratings={review}
            />
            <ToggleChartButton
              as="button"
              variant="text"
              onClick={() => setShowChart(false)}
            >
              {translations.showLess}
            </ToggleChartButton>
          </>
        ) : (
          <ToggleChartButton as="button" variant="text" onClick={openChart}>
            {translations.showMore}
          </ToggleChartButton>
        )}
      </footer>
    </article>
  );
};

export const ReviewOverlay = ({
  onClose,
  translations,
  localization,
  supplier,
  cityId,
  stateId,
  countryCode,
}: Props) => {
  const format = useFormatter();
  const {
    api: { host },
  } = useConfig();
  const { data, request } = http.useClientSideRequest<SupplierDetails | null>(
    host,
    http.API.customerReviews,
  );

  useEffect(() => {
    request('get', {
      supplier,
      localization,
      ...(cityId && { cityId }),
      ...(stateId && { stateId }),
      ...(countryCode && { countryCode }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier, localization, cityId, stateId, countryCode]);

  const title = (
    <Title>
      {translations.title} <Name>{supplier}</Name>
    </Title>
  );

  if (data === null) {
    return (
      <Ui.Overlay
        title={title}
        titlePosition="left"
        open
        width="wide"
        onClose={onClose}
      >
        <Ui.LoadingCircle />
      </Ui.Overlay>
    );
  }

  return (
    <Ui.Overlay
      title={title}
      titlePosition="left"
      open
      width="wide"
      onClose={onClose}
    >
      <Summary>
        <StyledThirdPartyLogo name={supplier} withPadding="vertical" />
        <AggregatedRating>
          <RatingLine
            value={formatValue(format, data.summary.averageRating.value)}
          >
            <CurrentRating>
              {formatValue(format, data.summary.averageRating.value)}
            </CurrentRating>
            /{formatValue(format, data.summary.averageRating.max)}
          </RatingLine>
          <Stars
            value={data.summary.averageRating.value}
            label={translations.stars}
          />
          <div>
            {data.summary.count > 1
              ? `${data.summary.count} ${translations.ratings}`
              : `${data.summary.count} ${translations.rating}`}
          </div>
        </AggregatedRating>
        <RatingsList>
          {[
            [data.summary.star5Count, 5],
            [data.summary.star4Count, 4],
            [data.summary.star3Count, 3],
            [data.summary.star2Count, 2],
            [data.summary.star1Count, 1],
          ].map(([count, value]) => (
            <Ui.RatingBar
              key={`rating-${value}`}
              label={
                <Stars value={value} label={translations.stars} withSpacing />
              }
              value={count}
              maxValue={data.summary.count}
              unformattedValue
            />
          ))}
        </RatingsList>
      </Summary>
      <ReviewList>
        {/* TODO: use better key than date */}
        {data.list.map(review => (
          <Review
            key={review.date}
            review={review}
            translations={translations}
          />
        ))}
      </ReviewList>
    </Ui.Overlay>
  );
};
